import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { MessageCircle } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { reveal, registerScrollTrigger } from '../lib/motion';

import { whatsappLinkProps, whatsappUrl, enquiryMessage, GENERAL_ORDER_MESSAGE } from '../lib/whatsapp';

const orderLink = whatsappLinkProps(GENERAL_ORDER_MESSAGE);

const labelStyle = {
  fontFamily: 'Tenor Sans',
  fontStyle: 'normal',
  fontSize: '11px',
  letterSpacing: '2.5px',
  textTransform: 'uppercase',
  color: 'var(--gold-dark)',
  display: 'block',
  marginBottom: '10px',
};

const ContactOrder = () => {
  const sectionRef = useRef(null);
  const titleRef   = useRef(null);
  const formRef    = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', scent: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sentUrl, setSentUrl] = useState('');

  useEffect(() => {
    registerScrollTrigger();
    const ctx = gsap.context(() => {
      reveal(titleRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out', scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } }
      );
      reveal(formRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1.1, ease: 'power2.out', delay: 0.2, scrollTrigger: { trigger: formRef.current, start: 'top 80%' } }
      );
    }, sectionRef);

    // The quiz pre-fills the scent dropdown through this event
    const onSelectScent = (e) => setForm(f => ({ ...f, scent: e.detail }));
    window.addEventListener('vael:selectScent', onSelectScent);

    return () => {
      ctx.revert();
      window.removeEventListener('vael:selectScent', onSelectScent);
    };
  }, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // Submits into WhatsApp rather than nowhere. This previously only flipped to
  // a success state while discarding the enquiry, which meant the "we'll be in
  // touch within 24 hours" copy was untrue and every lead was lost.
  //
  // window.open runs inside the submit gesture so browsers allow it, but popup
  // blockers and in-app webviews can still intervene — hence the explicit
  // fallback link on the success panel.
  const handleSubmit = (e) => {
    e.preventDefault();
    const url = whatsappUrl(enquiryMessage(form));
    setSentUrl(url);
    window.open(url, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      aria-label="Acquire your scent"
      className="section-pad"
      style={{ background: 'var(--deep-warm-black)' }}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Heading */}
        <div ref={titleRef} style={{ opacity: 0, marginBottom: '72px', textAlign: 'center' }}>
          <span className="section-eyebrow">06 — Order</span>
          <h2 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '5px', color: 'var(--cream)', fontWeight: 400, marginTop: '18px', marginBottom: '20px', textTransform: 'uppercase' }}>
            Acquire Your Scent
          </h2>
          <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '18px', color: 'var(--grey)', lineHeight: 1.8 }}>
            Every order is placed over WhatsApp — use the button, or send the form and
            <br />we'll open it for you. Toronto pop-ups and select wholesale partners.
          </p>
          <div className="rule-row" style={{ marginTop: '22px' }}>
            <div className="rule-row-line" />
            <span className="rule-row-label">Toronto pop-ups · Select wholesale</span>
            <div className="rule-row-line" />
          </div>
        </div>

        {/* WhatsApp CTA + divider — hidden once the form is submitted so the
            success panel isn't competing with a second call to action. */}
        {!submitted && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <a
                {...orderLink}
                data-testid="whatsapp-cta-btn"
                className="btn-whatsapp"
              >
                <MessageCircle size={16} aria-hidden="true" />
                Order via WhatsApp
              </a>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '52px' }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(201,169,110,0.1)' }} />
              <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--grey)', whiteSpace: 'nowrap' }}>
                or send a message
              </span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(201,169,110,0.1)' }} />
            </div>
          </>
        )}

        {/* Form */}
        <div ref={formRef} style={{ opacity: 0 }}>
          {submitted ? (
            <div data-testid="form-success" role="status" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '24px' }}>
                <div style={{ width: '2px', height: '40px', background: 'var(--gold)' }} />
                <h3 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '22px', letterSpacing: '4px', color: 'var(--cream)', fontWeight: 400 }}>
                  Over to WhatsApp
                </h3>
                <div style={{ width: '2px', height: '40px', background: 'var(--gold)' }} />
              </div>
              <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '18px', color: 'var(--grey)', lineHeight: 1.8, marginBottom: '36px' }}>
                We've opened WhatsApp with your details ready to send.
                <br />Send the message and we'll reply within 24 hours.
              </p>
              <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href={sentUrl || orderLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp"
                  data-testid="contact-whatsapp-fallback"
                >
                  <MessageCircle size={16} aria-hidden="true" />
                  Didn't open? Tap here
                </a>
                <button onClick={() => setSubmitted(false)} className="btn-gold" style={{ border: '1px solid var(--gold)', cursor: 'pointer' }}>
                  Send Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

              {/* Name + Email row */}
              <div className="contact-row">
                <div>
                  <label htmlFor="contact-name" style={labelStyle}>Name</label>
                  <input id="contact-name" type="text" name="name" required autoComplete="name" value={form.name} onChange={handleChange} placeholder="Your name" className="vael-input" data-testid="contact-name" />
                </div>
                <div>
                  <label htmlFor="contact-email" style={labelStyle}>Email</label>
                  <input id="contact-email" type="email" name="email" required autoComplete="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className="vael-input" data-testid="contact-email" />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="contact-phone" style={labelStyle}>Phone (optional)</label>
                <input id="contact-phone" type="tel" name="phone" autoComplete="tel" value={form.phone} onChange={handleChange} placeholder="+1 (xxx) xxx-xxxx" className="vael-input" data-testid="contact-phone" />
              </div>

              {/* Scent dropdown */}
              <div>
                <label htmlFor="contact-scent" style={labelStyle}>Scent of Interest</label>
                <div style={{ position: 'relative' }}>
                  <select
                    id="contact-scent"
                    name="scent"
                    value={form.scent}
                    onChange={handleChange}
                    data-testid="contact-scent"
                    className="vael-input vael-select"
                    style={{ padding: '14px 32px 14px 0', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
                  >
                    <option value="">Select a fragrance…</option>
                    {PRODUCTS.map(p => (
                      <option key={p.id} value={p.name}>
                        {p.name} — {p.inspired_by}
                      </option>
                    ))}
                  </select>
                  <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                      <path d="M1 1L5 5L9 1" stroke="#C9A96E" strokeWidth="1.2" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="contact-message" style={labelStyle}>Message</label>
                <textarea id="contact-message" name="message" required value={form.message} onChange={handleChange} placeholder="Tell us about your order, quantity, or any questions…" rows={5} className="vael-input" data-testid="contact-message" />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '14px', color: 'var(--grey-dim)', maxWidth: '400px', lineHeight: 1.6 }}>
                  All fragrances are original compositions inspired by designer scents. Not affiliated with any designer brand.
                </p>
                <button type="submit" className="btn-gold" data-testid="contact-submit-btn" style={{ border: '1px solid var(--gold)', cursor: 'pointer' }}>
                  Send via WhatsApp
                </button>
              </div>

            </form>
          )}
        </div>

      </div>
    </section>
  );
};

export default ContactOrder;
